import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ErroApi } from '../lib/api';
import {
  buscarQuadro,
  criarCard,
  criarColuna,
  formatarData,
  moverCard,
  removerColuna,
  salvarColuna,
  CORES_DA_ETAPA,
  NOME_DA_ETAPA,
  type CardResumo,
  type Quadro as QuadroDados,
} from '../lib/quadro';
import { Alerta } from '../componentes/Interface';
import { useConfirmacao } from '../componentes/Confirmacao';
import CardDetalhe from '../componentes/CardDetalhe';
import Layout from '../componentes/Layout';

const SEGUNDOS_ENTRE_CONSULTAS = 25;

export default function Quadro() {
  const { chave: chaveDaRota } = useParams();
  const chave = chaveDaRota ?? 'midia';
  const [dados, setDados] = useState<QuadroDados | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [abertoId, setAbertoId] = useState<number | null>(null);
  const [criandoEm, setCriandoEm] = useState<string | null>(null);
  const [novaColuna, setNovaColuna] = useState<string | null>(null);
  const [renomeando, setRenomeando] = useState<{ id: number; nome: string } | null>(null);
  const [titulo, setTitulo] = useState('');
  const [pessoas, setPessoas] = useState<{ id: number; nome: string }[]>([]);
  const [salvoEm, setSalvoEm] = useState<Date | null>(null);
  const [arrastando, setArrastando] = useState<number | null>(null);
  const [alvo, setAlvo] = useState<number | null>(null);
  const retrato = useRef<string>('');
  // Enter e Esc ja resolveram o nome; o blur que vem depois nao pode salvar de novo
  const renomeTratado = useRef(false);
  const { confirmar, dialogo } = useConfirmacao();

  /**
   * ARMADILHA 9: so troca o estado quando o conteudo mudou de verdade.
   * Sem essa comparacao, cada consulta redesenharia o quadro inteiro.
   */
  const carregar = useCallback(async (silencioso = false) => {
    try {
      const novo = await buscarQuadro(chave);
      const assinatura = JSON.stringify(novo);
      if (assinatura !== retrato.current) {
        retrato.current = assinatura;
        setDados(novo);
      }
      setErro(null);
    } catch (e) {
      if (!silencioso) setErro(e instanceof ErroApi ? e.message : 'Não consegui carregar o quadro.');
    }
  }, [chave]);

  useEffect(() => {
    retrato.current = '';
    void carregar();
    const t = setInterval(() => void carregar(true), SEGUNDOS_ENTRE_CONSULTAS * 1000);
    return () => clearInterval(t);
  }, [carregar]);

  useEffect(() => {
    api<{ pessoas: { id: number; nome: string }[] }>('/pessoas/equipe')
      .then((d) => setPessoas(d.pessoas))
      .catch(() => setPessoas([]));
  }, []);

  const porColuna = useMemo(() => {
    const mapa = new Map<number, CardResumo[]>();
    for (const c of dados?.cards ?? []) {
      const lista = mapa.get(c.colunaId) ?? [];
      lista.push(c);
      mapa.set(c.colunaId, lista);
    }
    return mapa;
  }, [dados]);

  const podeEditar = Boolean(dados?.podeEditar);

  async function comAviso(acao: () => Promise<unknown>) {
    try {
      await acao();
      await carregar();
      setSalvoEm(new Date());
      setErro(null);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui salvar.');
    }
  }

  /** Arrastar e soltar entre listas. Quem grava a mudanca e o servidor. */
  async function soltarEm(colunaChave: string, colunaId: number) {
    const id = arrastando;
    setArrastando(null);
    setAlvo(null);
    if (!id || !podeEditar) return;
    const card = (dados?.cards ?? []).find((c) => c.id === id);
    if (!card || card.colunaId === colunaId) return;
    await comAviso(() => moverCard(id, colunaChave));
  }

  async function adicionarCard(colunaChave: string) {
    if (!titulo.trim()) return;
    await comAviso(async () => {
      await criarCard(titulo.trim(), colunaChave, chave);
      setTitulo('');
      setCriandoEm(null);
    });
  }

  async function moverColuna(indice: number, direcao: -1 | 1) {
    const colunas = dados?.colunas ?? [];
    const atual = colunas[indice];
    const vizinha = colunas[indice + direcao];
    if (!atual || !vizinha) return;
    await comAviso(async () => {
      await salvarColuna(atual.id, { ordem: vizinha.ordem });
      await salvarColuna(vizinha.id, { ordem: atual.ordem });
    });
  }

  /** Renomear troca so o rotulo da lista: a chave fica, entao os cards nao se mexem. */
  async function renomearColuna(id: number, nome: string) {
    const limpo = nome.trim();
    const coluna = dados?.colunas.find((c) => c.id === id);
    setRenomeando(null);
    if (!coluna || !limpo || limpo === coluna.nome) return;
    await comAviso(() => salvarColuna(id, { nome: limpo }));
  }

  async function adicionarColuna(nome: string) {
    const limpo = nome.trim();
    if (!limpo) return;
    await comAviso(async () => {
      await criarColuna(limpo, chave);
      setNovaColuna(null);
    });
  }

  async function excluirColuna(id: number, nome: string) {
    const ok = await confirmar({
      titulo: `Remover a coluna "${nome}"?`,
      texto: 'So da para remover coluna vazia. Colunas de etapa fixa não saem.',
      confirmar: 'Remover',
    });
    if (!ok) return;
    await comAviso(() => removerColuna(id));
  }

  return (
    <Layout
      titulo={dados?.quadro.nome ?? 'Quadro'}
      subtitulo="Arraste o card de uma lista para outra. O sistema anota quem mexeu e quando."
      acoes={
        <span className="flex items-center gap-2 text-sm text-tinta-400">
          {salvoEm ? (
            <>
              Tudo salvo
              <svg viewBox="0 0 24 24" className="h-4 w-4 text-tinta-900" fill="none" stroke="currentColor" strokeWidth="3">
                <path d="M5 13l4 4L19 7" />
              </svg>
            </>
          ) : (
            'Sincronizado'
          )}
        </span>
      }
    >
      {erro && (
        <div className="mb-4 max-w-xl">
          <Alerta>{erro}</Alerta>
        </div>
      )}
      {dados && !podeEditar && (
        <div className="mb-4 max-w-xl">
          <Alerta tipo="aviso">Seu acesso e de leitura: você ve tudo, mas não edita.</Alerta>
        </div>
      )}

      <div className="rolagem-suave flex items-start gap-3 overflow-x-auto pb-6">
        {(dados?.colunas ?? []).map((coluna, indice) => {
          const cards = porColuna.get(coluna.id) ?? [];
          return (
            <section
              key={coluna.id}
              onDragOver={(e) => {
                if (!podeEditar || arrastando === null) return;
                e.preventDefault();
                setAlvo(coluna.id);
              }}
              onDragLeave={() => setAlvo((a) => (a === coluna.id ? null : a))}
              onDrop={(e) => {
                e.preventDefault();
                void soltarEm(coluna.chave, coluna.id);
              }}
              className={`w-[80vw] max-w-[18rem] shrink-0 rounded-2xl bg-white/70 p-2 ring-1 transition sm:w-[15.5rem] ${
                alvo === coluna.id ? 'bg-white ring-2 ring-tinta-900' : 'ring-tinta-200'
              }`}
            >
              <div className="mb-2 flex items-center justify-between gap-1 px-1">
                {renomeando?.id === coluna.id ? (
                  <input
                    autoFocus
                    value={renomeando.nome}
                    maxLength={80}
                    aria-label={`Novo nome da coluna ${coluna.nome}`}
                    onChange={(e) => setRenomeando({ id: coluna.id, nome: e.target.value })}
                    onBlur={() => {
                      if (renomeTratado.current) {
                        renomeTratado.current = false;
                        return;
                      }
                      void renomearColuna(coluna.id, renomeando.nome);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        renomeTratado.current = true;
                        void renomearColuna(coluna.id, renomeando.nome);
                      }
                      if (e.key === 'Escape') {
                        renomeTratado.current = true;
                        setRenomeando(null);
                      }
                    }}
                    className="w-full rounded-lg border border-tinta-200 bg-white px-2 py-1 text-sm font-semibold text-tinta-900"
                  />
                ) : (
                  <h2 className="flex min-w-0 items-center gap-1 text-sm font-semibold text-tinta-900">
                    {podeEditar ? (
                      <button
                        onClick={() => setRenomeando({ id: coluna.id, nome: coluna.nome })}
                        title="Renomear a coluna"
                        className="truncate rounded px-1 text-left hover:bg-tinta-100"
                      >
                        {coluna.nome}
                      </button>
                    ) : (
                      <span className="truncate">{coluna.nome}</span>
                    )}
                    <span className="shrink-0 text-tinta-400">({cards.length})</span>
                  </h2>
                )}
                {podeEditar && renomeando?.id !== coluna.id && (
                  <div className="flex shrink-0 items-center gap-1 text-tinta-400">
                    <button
                      onClick={() => void moverColuna(indice, -1)}
                      className="rounded px-1 hover:bg-tinta-100 hover:text-tinta-900"
                      title="Mover para a esquerda"
                    >
                      &larr;
                    </button>
                    <button
                      onClick={() => void moverColuna(indice, 1)}
                      className="rounded px-1 hover:bg-tinta-100 hover:text-tinta-900"
                      title="Mover para a direita"
                    >
                      &rarr;
                    </button>
                    <button
                      onClick={() => void excluirColuna(coluna.id, coluna.nome)}
                      className="rounded px-1 text-red-400 hover:bg-red-50 hover:text-red-600"
                      title="Remover coluna"
                    >
                      &times;
                    </button>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                {cards.map((card) => (
                  // a chave e o id do card, nunca o indice da lista
                  <article
                    key={card.id}
                    draggable={podeEditar}
                    onDragStart={(e) => {
                      setArrastando(card.id);
                      e.dataTransfer.effectAllowed = 'move';
                      e.dataTransfer.setData('text/plain', String(card.id));
                    }}
                    onDragEnd={() => {
                      setArrastando(null);
                      setAlvo(null);
                    }}
                    onClick={() => setAbertoId(card.id)}
                    className={`overflow-hidden rounded-xl bg-white shadow-cartao transition hover:-translate-y-0.5 ${
                      podeEditar ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'
                    } ${arrastando === card.id ? 'opacity-40' : ''}`}
                  >
                    {card.capaCaminho && (
                      // a capa encosta nas bordas do card e aparece inteira, no formato dela
                      <img
                        src={`/api/uploads/${card.capaCaminho}`}
                        alt=""
                        loading="lazy"
                        className="block h-auto w-full rounded-t-xl object-contain"
                      />
                    )}

                    <div className="p-2.5">

                    <div className="mb-2 flex items-center justify-between gap-2">
                      <span
                        className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${
                          CORES_DA_ETAPA[card.etapa] ?? 'bg-slate-100 text-slate-600'
                        }`}
                      >
                        {NOME_DA_ETAPA[card.etapa] ?? card.etapa}
                      </span>
                      {card.prioridade === 'urgente' && (
                        <span className="rounded-md bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">
                          urgente
                        </span>
                      )}
                    </div>

                    <h3 className="text-sm font-semibold leading-snug text-tinta-900">{card.titulo}</h3>

                    {card.tarefas.total > 0 && (
                      <div className="mt-2 flex items-center gap-2">
                        <div
                          className="h-1 flex-1 overflow-hidden rounded-full bg-tinta-200"
                          role="progressbar"
                          aria-valuenow={card.tarefas.feitas}
                          aria-valuemin={0}
                          aria-valuemax={card.tarefas.total}
                          aria-label={`${card.tarefas.feitas} de ${card.tarefas.total} tarefas`}
                        >
                          <div
                            className={`h-full rounded-full ${
                              card.tarefas.feitas === card.tarefas.total
                                ? 'bg-tinta-900'
                                : 'bg-tinta-400'
                            }`}
                            style={{
                              width: `${(card.tarefas.feitas / card.tarefas.total) * 100}%`,
                            }}
                          />
                        </div>
                        <span className="shrink-0 text-[11px] tabular-nums text-tinta-400">
                          {card.tarefas.feitas}/{card.tarefas.total}
                        </span>
                      </div>
                    )}

                    <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px]">
                      {card.entregaEm && (
                        <span className="text-tinta-400">📅 {formatarData(card.entregaEm)}</span>
                      )}
                      {card.linkPronto && (
                        <a
                          href={card.linkPronto}
                          target="_blank"
                          rel="noopener noreferrer nofollow"
                          onClick={(e) => e.stopPropagation()}
                          className="font-medium text-tinta-900 underline decoration-tinta-200 underline-offset-2 hover:decoration-tinta-900"
                        >
                          🔗 {card.linkNome || 'Abrir arquivo'}
                        </a>
                      )}
                      {card.checklist.length > 0 && (
                        <span className="text-tinta-400">
                          ✓ {card.checklist.filter((i) => i.feito).length}/{card.checklist.length}
                        </span>
                      )}
                    </div>

                    {card.responsaveis.length > 0 && (
                      <div className="mt-2 flex -space-x-1.5">
                        {card.responsaveis.map((r) => (
                          <span
                            key={r.id}
                            title={r.nome}
                            className="flex h-5 w-5 items-center justify-center rounded-full border-2 border-white bg-tinta-900 text-[9px] font-semibold uppercase text-white"
                          >
                            {r.nome.trim().charAt(0)}
                          </span>
                        ))}
                      </div>
                    )}
                    </div>
                  </article>
                ))}

                {podeEditar &&
                  (criandoEm === coluna.chave ? (
                    <div className="rounded-2xl bg-white p-2.5 shadow-cartao">
                      <input
                        autoFocus
                        value={titulo}
                        onChange={(e) => setTitulo(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') void adicionarCard(coluna.chave);
                          if (e.key === 'Escape') setCriandoEm(null);
                        }}
                        placeholder="Título do card"
                        className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm"
                      />
                      <div className="mt-2 flex gap-2 text-sm">
                        <button
                          onClick={() => void adicionarCard(coluna.chave)}
                          className="rounded-lg bg-tinta-900 px-3 py-1.5 font-medium text-white hover:bg-tinta-700"
                        >
                          Criar
                        </button>
                        <button
                          onClick={() => setCriandoEm(null)}
                          className="rounded-lg border border-tinta-200 px-3 py-1.5 text-tinta-600"
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        setCriandoEm(coluna.chave);
                        setTitulo('');
                      }}
                      className="w-full rounded-lg py-1.5 text-xs font-medium text-tinta-400 transition hover:bg-white hover:text-tinta-900"
                    >
                      + Adicionar card
                    </button>
                  ))}
              </div>
            </section>
          );
        })}

        {podeEditar && (
          <div className="w-[80vw] max-w-[18rem] shrink-0 sm:w-[15.5rem]">
            {novaColuna !== null ? (
              <div className="rounded-2xl border border-dashed border-tinta-200 bg-white/60 p-3">
                <input
                  autoFocus
                  value={novaColuna}
                  onChange={(e) => setNovaColuna(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void adicionarColuna(novaColuna);
                    if (e.key === 'Escape') setNovaColuna(null);
                  }}
                  placeholder="Nome da coluna"
                  className="w-full rounded-lg border border-tinta-200 bg-white px-2.5 py-1.5 text-sm text-tinta-900"
                />
                <p className="mt-2 text-xs text-tinta-400">Enter para criar, Esc para cancelar</p>
              </div>
            ) : (
              <button
                onClick={() => setNovaColuna('')}
                className="w-full rounded-2xl border border-dashed border-tinta-200 py-4 text-sm text-tinta-400 transition hover:border-tinta-900 hover:text-tinta-900"
              >
                + Adicionar coluna
              </button>
            )}
          </div>
        )}
      </div>

      {abertoId !== null && (
        <CardDetalhe
          cardId={abertoId}
          nomeQuadro={dados?.quadro.nome ?? ''}
          podeEditar={podeEditar}
          pessoas={pessoas}
          colunas={dados?.colunas ?? []}
          aoFechar={() => {
            setAbertoId(null);
            void carregar();
          }}
          aoMudar={() => {
            setSalvoEm(new Date());
            void carregar();
          }}
        />
      )}
      {dialogo}
    </Layout>
  );
}
